import { useState } from "react";
import { useAuth } from "../auth";
import { Btn, Field, Input } from "../components/ui";
import Icon from "../components/Icon";
import { BRAND, HERO_GRAD } from "../theme";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try { await login(email, password); window.location.hash = ""; }
    catch (ex) { setErr(ex.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Form side */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-500/25" style={{ backgroundColor: BRAND }}>
              <Icon name="cap" size={20} />
            </div>
            <span className="font-bold text-slate-900 text-lg tracking-tight">Edestia</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Welcome back</h1>
          <p className="text-slate-500 mt-2 text-sm">Sign in with the credentials issued to you.</p>
          <div className="mt-8 space-y-5">
            <Field label="Email"><Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@example.com" /></Field>
            <Field label="Password">
              <div className="relative">
                <Input value={password} onChange={(e) => setPassword(e.target.value)} type={showPw ? "text" : "password"} placeholder="Your password" className="pr-10" />
                <button type="button" onClick={() => setShowPw((s) => !s)} tabIndex={-1} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600">
                  <Icon name={showPw ? "eye-off" : "eye"} size={18} />
                </button>
              </div>
            </Field>
            {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3.5 py-2.5">{err}</div>}
            <Btn type="submit" className="w-full py-3" disabled={busy}>{busy ? "Signing in…" : "Log in"}</Btn>
          </div>
        </form>
      </div>

      {/* Hero side */}
      <div className="hidden lg:flex items-center justify-center p-12 text-white relative overflow-hidden" style={{ backgroundImage: HERO_GRAD }}>
        <div className="max-w-md relative z-10">
          <div className="text-xs uppercase tracking-[0.2em] text-indigo-300 font-semibold">Learn · Progress · Achieve</div>
          <h2 className="mt-4 text-4xl font-bold leading-tight">Your learning journey, organised in one place.</h2>
          <ul className="mt-10 space-y-4 text-sm text-indigo-100">
            <li className="flex items-start gap-3"><div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center mt-0.5 shrink-0"><Icon name="check" size={12} /></div>Access your course modules and learning resources</li>
            <li className="flex items-start gap-3"><div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center mt-0.5 shrink-0"><Icon name="check" size={12} /></div>Complete lessons and assessments at your own pace</li>
            <li className="flex items-start gap-3"><div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center mt-0.5 shrink-0"><Icon name="check" size={12} /></div>Track your learning progress and achievements</li>
            <li className="flex items-start gap-3"><div className="w-5 h-5 rounded-full bg-white/15 flex items-center justify-center mt-0.5 shrink-0"><Icon name="check" size={12} /></div>Earn your certificate upon successful completion</li>
          </ul>
        </div>
        {/* Decorative elements */}
        <div className="absolute -right-20 -bottom-20 w-80 h-80 rounded-full bg-white/[0.03]" />
        <div className="absolute right-40 bottom-20 w-20 h-20 rounded-full bg-white/[0.04]" />
        <div className="absolute -left-10 top-20 w-40 h-40 rounded-full bg-white/[0.02]" />
      </div>
    </div>
  );
}
